import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import { Bar } from "react-chartjs-2";
import moment from "moment";
import { getDashboardData } from "../../services/_dashboard";
import Spinner from "../../layouts/spinner";
import { getMe } from "context/AuthContext";
import { useNavigate } from "react-router-dom";

const today = moment().format("YYYY-MM-DD");
const startOfYear = moment().startOf("year").format("YYYY-MM-DD");

const DEFAULT_FILTERS = {
  globalFromDate: startOfYear,
  globalToDate: today,
  globalPartner: "DEFAULT",

  totalDateFrom: null,
  totalDateTo: null,

  symptomsFrom: "",
  symptomsTo: "",
  symptomsDateFrom: null,
  symptomsDateTo: null,

  diagnosticsFrom: "",
  diagnosticsTo: "",
  diagnosticsDateFrom: null,
  diagnosticsDateTo: null,

  combinedSymFrom: "",
  combinedSymTo: "",
  combinedDiagFrom: "",
  combinedDiagTo: "",
  combinedDateFrom: null,
  combinedDateTo: null,

  ageFrom: "",
  ageTo: "",
  ageDateFrom: null,
  ageDateTo: null,
};

const Dashboard = () => {
  const isMobile = window.innerWidth < 768;
  const user = React.useMemo(() => getMe(), []);

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const handleLogout = useCallback(async () => {
    localStorage.removeItem("token");
    localStorage.removeItem("permissions");
    localStorage.removeItem("me");
    navigate("/auth/login");
  }, [navigate]);

  useEffect(() => {
    if (!["DEFAULT", "SAA"].includes(user.partner)) {
      handleLogout();
    }
  }, [handleLogout, user.partner]);

  const effectivePartner =
    user.partner !== "DEFAULT" ? user.partner : appliedFilters.globalPartner;
  const [partners] = useState(["DEFAULT", "SAA"]);

  const [dashboardData, setDashboardData] = useState({
    totalSurvey: [],
    symptoms: [],
    diagnostics: [],
    combined: [],
    age: [],
  });

  const applyFilters = () => {
    setAppliedFilters(draftFilters);
  };

  const resetAllFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  // Fetch data manually
  const fetchData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const q = new URLSearchParams();

      q.append("from_date", appliedFilters.globalFromDate);
      q.append("to_date", appliedFilters.globalToDate);

      const partner =
        user.partner !== "DEFAULT"
          ? user.partner
          : appliedFilters.globalPartner;
      q.append("partner", partner);

      if (appliedFilters.totalDateFrom)
        q.append("total_date_from", appliedFilters.totalDateFrom);
      if (appliedFilters.totalDateTo)
        q.append("total_date_to", appliedFilters.totalDateTo);

      if (appliedFilters.symptomsFrom)
        q.append("symptoms_from", appliedFilters.symptomsFrom);
      if (appliedFilters.symptomsTo)
        q.append("symptoms_to", appliedFilters.symptomsTo);
      if (appliedFilters.symptomsDateFrom)
        q.append("symptoms_date_from", appliedFilters.symptomsDateFrom);
      if (appliedFilters.symptomsDateTo)
        q.append("symptoms_date_to", appliedFilters.symptomsDateTo);

      if (appliedFilters.diagnosticsFrom)
        q.append("diagnostics_from", appliedFilters.diagnosticsFrom);
      if (appliedFilters.diagnosticsTo)
        q.append("diagnostics_to", appliedFilters.diagnosticsTo);
      if (appliedFilters.diagnosticsDateFrom)
        q.append("diagnostics_date_from", appliedFilters.diagnosticsDateFrom);
      if (appliedFilters.diagnosticsDateTo)
        q.append("diagnostics_date_to", appliedFilters.diagnosticsDateTo);

      if (appliedFilters.combinedSymFrom)
        q.append("combined_symptoms_from", appliedFilters.combinedSymFrom);
      if (appliedFilters.combinedSymTo)
        q.append("combined_symptoms_to", appliedFilters.combinedSymTo);
      if (appliedFilters.combinedDiagFrom)
        q.append("combined_diagnostics_from", appliedFilters.combinedDiagFrom);
      if (appliedFilters.combinedDiagTo)
        q.append("combined_diagnostics_to", appliedFilters.combinedDiagTo);
      if (appliedFilters.combinedDateFrom)
        q.append("combined_date_from", appliedFilters.combinedDateFrom);
      if (appliedFilters.combinedDateTo)
        q.append("combined_date_to", appliedFilters.combinedDateTo);

      if (appliedFilters.ageDateFrom) {
        q.append("age_date_from", appliedFilters.ageDateFrom);
      }

      if (appliedFilters.ageDateTo) {
        q.append("age_date_to", appliedFilters.ageDateTo);
      }
      if (appliedFilters.ageFrom) q.append("age_from", appliedFilters.ageFrom);
      if (appliedFilters.ageTo) q.append("age_to", appliedFilters.ageTo);

      const res = await getDashboardData(q.toString());
      setDashboardData(res.data);
    } catch (e) {
      console.error("Dashboard fetch failed", e);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!user) return <Spinner isFullScreen={true} />;

  const formatMonthLabel = (month, year) =>
    moment(`${year}-${month}-01`).format("MMM YYYY");

  const buildChartData = (data = [], label) => ({
    labels: data.map((d) => formatMonthLabel(d.month, d.year)),
    datasets: [
      {
        label,
        data: data.map((d) => d.count),
        backgroundColor: "#5e72e4",
      },
    ],
  });

  const getBarOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  });

  return (
    <>
      <Header />
      <Container className="mt--7 px-3 px-md-0" fluid>
        <Card className="shadow">
          <Row className="mb-4 mt-4 mx-0 gx-3 gx-md-4">
            <Col xs="12" sm="6" md="3" className="mb-2 mb-md-0">
              <FormGroup>
                <Label
                  style={{
                    color: "#32325d",
                    fontWeight: "bolder",
                  }}
                >
                  From Date :
                </Label>
                <Input
                  type="date"
                  value={draftFilters.globalFromDate}
                  onChange={(e) =>
                    setDraftFilters((f) => ({
                      ...f,
                      globalFromDate: e.target.value,
                    }))
                  }
                />
              </FormGroup>
            </Col>
            <Col xs="12" sm="6" md="3" className="mb-2 mb-md-0">
              <FormGroup>
                <Label style={{ color: "#32325d", fontWeight: "bolder" }}>
                  To Date :
                </Label>
                <Input
                  type="date"
                  value={draftFilters.globalToDate}
                  max={today}
                  onChange={(e) =>
                    setDraftFilters((f) => ({
                      ...f,
                      globalToDate: e.target.value,
                    }))
                  }
                />
              </FormGroup>
            </Col>
            {user.partner === "DEFAULT" && (
              <Col xs="12" sm="6" md="3" className="mb-2 mb-md-0">
                <FormGroup>
                  <Label style={{ color: "#32325d", fontWeight: "bolder" }}>
                    Partner :
                  </Label>
                  <Input
                    type="select"
                    value={draftFilters.globalPartner}
                    onChange={(e) =>
                      setDraftFilters((f) => ({
                        ...f,
                        globalPartner: e.target.value,
                      }))
                    }
                  >
                    {partners.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            )}
            <Col md="3" className="d-flex flex-wrap align-items-center gap-2">
              <Button color="primary" onClick={applyFilters} disabled={loading}>
                {loading ? "Loading..." : "Apply Filters"}
              </Button>

              <Button color="info" onClick={resetAllFilters} disabled={loading}>
                Reset
              </Button>
            </Col>
          </Row>

          {loading && <Spinner isFullScreen={false} />}

          <Row>
            <Col xl="6" className="mb-4">
              <Card className="shadow">
                <CardHeader>
                  <h3 className="mb-0">Total Surveys (Monthly)</h3>
                </CardHeader>
                <CardBody>
                  <Row form className="mb-2 g-2">
                    <Col md="6" sm="6" xs="12" className="mb-2">
                      <Input
                        type="date"
                        value={
                          draftFilters.totalDateFrom ??
                          draftFilters.globalFromDate
                        }
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            totalDateFrom: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="6" sm="6" xs="12" className="mb-2">
                      <Input
                        type="date"
                        value={
                          draftFilters.totalDateTo ?? draftFilters.globalToDate
                        }
                        max={today}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            totalDateTo: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    {/* <Col md="3">
                    <Input
                      type="number"
                      placeholder="Min %"
                      value={totalFrom}
                      onChange={(e) => setTotalFrom(e.target.value)}
                    />
                  </Col>
                  <Col md="3">
                    <Input
                      type="number"
                      placeholder="Max %"
                      value={totalTo}
                      onChange={(e) => setTotalTo(e.target.value)}
                    />
                  </Col> */}
                  </Row>
                  <div
                    className="position-relative w-100"
                    style={{ height: isMobile ? "260px" : "350px" }}
                  >
                    <Bar
                      data={buildChartData(
                        dashboardData.totalSurvey,
                        "Surveys"
                      )}
                      options={getBarOptions()}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl="6" className="mb-4">
              <Card className="shadow">
                <CardHeader>
                  <h3 className="mb-0">Symptoms Results (Monthly)</h3>
                </CardHeader>
                <CardBody>
                  <Row form className="mb-2 g-2">
                    <Col md="3" sm="6" xs="12" className="mb-2">
                      <Input
                        type="date"
                        value={
                          draftFilters.symptomsDateFrom ??
                          draftFilters.globalFromDate
                        }
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            symptomsDateFrom: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="3" sm="6" xs="12" className="mb-2">
                      <Input
                        type="date"
                        value={
                          draftFilters.symptomsDateTo ??
                          draftFilters.globalToDate
                        }
                        max={today}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            symptomsDateTo: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="3" sm="6" xs="12" className="mb-2">
                      <Input
                        type="number"
                        placeholder="Min %"
                        value={draftFilters.symptomsFrom}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            symptomsFrom: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="3" sm="6" xs="12" className="mb-2">
                      <Input
                        type="number"
                        placeholder="Max %"
                        value={draftFilters.symptomsTo}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            symptomsTo: e.target.value,
                          }))
                        }
                      />
                    </Col>
                  </Row>
                  <div
                    className="position-relative w-100"
                    style={{ height: isMobile ? "260px" : "350px" }}
                  >
                    <Bar
                      data={buildChartData(dashboardData.symptoms, "Symptoms")}
                      options={getBarOptions()}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl="6" className="mb-4">
              <Card className="shadow">
                <CardHeader>
                  <h3 className="mb-0">Diagnostics Results (Monthly)</h3>
                </CardHeader>
                <CardBody>
                  <Row form className="mb-2 g-2">
                    <Col md="6" sm="6" xs="12" className="mb-2">
                      <Input
                        type="date"
                        value={
                          draftFilters.diagnosticsDateFrom ??
                          draftFilters.globalFromDate
                        }
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            diagnosticsDateFrom: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="6" sm="6" xs="12" className="mb-2">
                      <Input
                        type="date"
                        value={
                          draftFilters.diagnosticsDateTo ??
                          draftFilters.globalToDate
                        }
                        max={today}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            diagnosticsDateTo: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="6" sm="6" xs="12" className="mb-2">
                      <Input
                        type="number"
                        placeholder="Min %"
                        value={draftFilters.diagnosticsFrom}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            diagnosticsFrom: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="6" sm="6" xs="12" className="mb-2">
                      <Input
                        type="number"
                        placeholder="Max %"
                        value={draftFilters.diagnosticsTo}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            diagnosticsTo: e.target.value,
                          }))
                        }
                      />
                    </Col>
                  </Row>
                  <div
                    className="position-relative w-100"
                    style={{ height: isMobile ? "260px" : "350px" }}
                  >
                    <Bar
                      data={buildChartData(
                        dashboardData.diagnostics,
                        "Diagnostics"
                      )}
                      options={getBarOptions()}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl="6" className="mb-4">
              <Card className="shadow">
                <CardHeader>
                  <h3 className="mb-0">
                    Symptoms & Diagnostics Results (Monthly)
                  </h3>
                </CardHeader>
                <CardBody>
                  <Row form className="mb-2 g-2">
                    <Col md="4" sm="6" xs="12" className="mb-2">
                      <Input
                        type="date"
                        value={
                          draftFilters.combinedDateFrom ??
                          draftFilters.globalFromDate
                        }
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            combinedDateFrom: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="4" sm="6" xs="12" className="mb-2">
                      <Input
                        type="date"
                        value={
                          draftFilters.combinedDateTo ??
                          draftFilters.globalToDate
                        }
                        max={today}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            combinedDateTo: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="4" sm="6" xs="12" className="mb-2">
                      <Input
                        type="number"
                        placeholder="Symptoms Min %"
                        value={draftFilters.combinedSymFrom}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            combinedSymFrom: e.target.value,
                          }))
                        }
                      />
                    </Col>

                    <Col md="4" sm="6" xs="12" className="mb-2">
                      <Input
                        type="number"
                        placeholder="Symptoms Max %"
                        value={draftFilters.combinedSymTo}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            combinedSymTo: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="4" sm="6" xs="12" className="mb-2">
                      <Input
                        type="number"
                        placeholder="Diagnostics Min %"
                        value={draftFilters.combinedDiagFrom}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            combinedDiagFrom: e.target.value,
                          }))
                        }
                      />
                    </Col>
                    <Col md="4" sm="6" xs="12" className="mb-2">
                      <Input
                        type="number"
                        placeholder="Diagnostics Max %"
                        value={draftFilters.combinedDiagTo}
                        onChange={(e) =>
                          setDraftFilters((f) => ({
                            ...f,
                            combinedDiagTo: e.target.value,
                          }))
                        }
                      />
                    </Col>
                  </Row>
                  <div
                    className="position-relative w-100"
                    style={{ height: isMobile ? "260px" : "350px" }}
                  >
                    <Bar
                      data={buildChartData(dashboardData.combined, "Combined")}
                      options={getBarOptions()}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>

            {effectivePartner === "SAA" && (
              <Col xl="6" className="mb-4">
                <Card className="shadow">
                  <CardHeader>
                    <h3 className="mb-0">Age - survey results (Monthly)</h3>
                  </CardHeader>
                  <CardBody>
                    <Row form className="mb-2 g-2">
                      <Col md="3" sm="6" xs="12" className="mb-2">
                        <Input
                          type="date"
                          value={
                            draftFilters.ageDateFrom ??
                            draftFilters.globalFromDate
                          }
                          onChange={(e) =>
                            setDraftFilters((f) => ({
                              ...f,
                              ageDateFrom: e.target.value,
                            }))
                          }
                        />
                      </Col>

                      <Col md="3" sm="6" xs="12" className="mb-2">
                        <Input
                          type="date"
                          value={
                            draftFilters.ageDateTo ?? draftFilters.globalToDate
                          }
                          max={today}
                          onChange={(e) =>
                            setDraftFilters((f) => ({
                              ...f,
                              ageDateTo: e.target.value,
                            }))
                          }
                        />
                      </Col>

                      <Col md="3" sm="6" xs="12" className="mb-2">
                        <Input
                          type="number"
                          placeholder="Age From"
                          value={draftFilters.ageFrom}
                          onChange={(e) =>
                            setDraftFilters((f) => ({
                              ...f,
                              ageFrom: e.target.value,
                            }))
                          }
                        />
                      </Col>

                      <Col md="3" sm="6" xs="12" className="mb-2">
                        <Input
                          type="number"
                          placeholder="Age To"
                          value={draftFilters.ageTo}
                          onChange={(e) =>
                            setDraftFilters((f) => ({
                              ...f,
                              ageTo: e.target.value,
                            }))
                          }
                        />
                      </Col>
                    </Row>

                    <div style={{ height: isMobile ? "260px" : "350px" }}>
                      <Bar
                        data={buildChartData(dashboardData.age, "Age")}
                        options={getBarOptions()}
                      />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Card>
      </Container>
    </>
  );
};

export default Dashboard;
